import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { API_BASE } from '../utils/apiBase';
import api from '../services/api';
import ringArt from '../assets/img/portal-ring.png';
import useMediaQuery from '../utils/useMediaQuery';
import usePrefersReducedMotion from '../hooks/usePrefersReducedMotion';
import messageService from '../services/messageService';
import { tintFor } from '../utils/tints';
import { shareImage, saveImage, isNative } from '../utils/nativeShare';
import LoadingSpinner from './LoadingSpinner';
import { friendService } from '../services';
import { brandedQrBlob } from '../utils/brandedQr';
import { generateSharePoster, POSTER_SIZES } from '../utils/sharePoster';
import * as htmlToImage from 'html-to-image';
import StoryCard from './share/StoryCard';
import ScaledCard from './share/ScaledCard';
import PosterThumb from './share/PosterThumb';
import ShareTemplatePicker from './share/ShareTemplatePicker';
import useShareTemplates from '../hooks/useShareTemplates';
import BrandedQR from './BrandedQR';
import usePageScrollLock from '../utils/usePageScrollLock';

const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

/**
 * SHARE EVENT — share-sheet handoff (Sept 2026).
 *
 * Tap the ring on a card cover: the ring bursts out of the button (`originRect`), the scrim fades
 * in, and the sheet rises behind it — a bottom sheet on phones, a 480px modal from 640. The
 * header ring spins counter-clockwise round the event cover (the Portal send ring's motion) and
 * the sections rise in, 60ms apart:
 *
 *   event summary · EVENT LINK (copy) · SEND TO FRIENDS ON PORTAL · socials · QR & posters
 *
 * Sending to friends plays the Portal send beat inside the sheet (SENDING THE EVENT → ON ITS
 * WAY, 1.8s, never cut short) while the messages go out, one 1:1 thread each, the same link
 * card SendToFriendsPanel sends. No borders anywhere in the sheet: #F2F2F2 fills and pills.
 *
 * What it always did stays: the SMS / WhatsApp / X / Facebook intents, the Instagram Story
 * generator, the disco-ball QR and its print posters, and the host-side INVITE FRIENDS list.
 * Esc, ✕, a scrim tap or (phone) a swipe down on the handle closes it — not mid-send. Body
 * scroll is locked while it's open, focus goes to ✕ and comes back to whoever opened it.
 */
const EASE_OUT = 'cubic-bezier(0,0,.2,1)';

/**
 * The spinning Portal ring round the event's cover. Module-level on purpose: declared inside
 * the sheet it was a new component every render, so it remounted — and replayed its pop — each
 * time the sheet re-rendered (friends loading, a keystroke, a pick).
 * Pop the wrapper, spin the art inside it: two transforms that must not fight.
 */
function SheetRing({ size, cover, ripple = false, reduced = false }) {
  const anim = (a) => (reduced ? 'none' : a);
  return (
    <span className="relative block flex-shrink-0" style={{ width: size, height: size }}>
      {ripple && !reduced && (
        <span className="absolute inset-0 rounded-full" style={{ border: '2px solid #00FFFF', animation: `ssRipple 1.4s ${EASE_OUT} 260ms 2 both` }} />
      )}
      <span className="absolute inset-0" style={{ animation: anim(`ssPop 420ms ${EASE_OUT} 80ms both`) }}>
        <span className="absolute inset-0 rounded-full" style={{ backgroundImage: `url(${ringArt})`, backgroundSize: 'cover', animation: anim('ssSpin 1.1s linear infinite') }} />
      </span>
      <span className="absolute overflow-hidden rounded-full" style={{ inset: '5.5%', background: '#1A1A1A', animation: anim(`ssPop 420ms ${EASE_OUT} 200ms both`) }}>
        {cover && <img src={cover} alt="" className="block h-full w-full object-cover" />}
      </span>
    </span>
  );
}


export default function ShareSheet({ isOpen, onClose, title, venue = null, city = null, url, inviteCode, date, status, mode, eventId, eventService, posterUrl, originRect = null, cardUrl }) {
  const [copyLinkSuccess, setCopyLinkSuccess] = useState(false);
  const [igGenerating, setIgGenerating] = useState(false);
  const [igPreview, setIgPreview] = useState(null); // { imageUrl, blob, fileName }
  const [igShareError, setIgShareError] = useState('');
  // Which overlay text pieces to draw on the story image. Unchecking one keeps the
  // poster clean when it already shows that info (no text-over-text).
  const [igInclude, setIgInclude] = useState({ branding: true, title: true, date: true, code: true, qr: true });

  // Print-poster download state (Phase 12).
  const [posterBusy, setPosterBusy] = useState(null); // sizeKey being generated

  const handleDownloadPoster = async (sizeKey) => {
    setPosterBusy(sizeKey);
    try {
      const dateLabel = date
        ? new Date(date).toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric' })
        : '';
      const { blob, fileName } = await generateSharePoster(
        { title, dateLabel, venue, city, url, code: inviteCode }, sizeKey, printTemplate, { poster: printPoster },
      );
      await saveImage({ blob, fileName });
    } catch (err) {
      console.error('Poster generation failed:', err);
    } finally {
      setPosterBusy(null);
    }
  };

  // Invite friends state
  const [invitableFriends, setInvitableFriends] = useState([]);
  const [inviteFriendsLoading, setInviteFriendsLoading] = useState(false);
  const [inviteLoading, setInviteLoading] = useState({});
  const [hasFetchedFriends, setHasFetchedFriends] = useState(false);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchTimeout = useRef(null);

  const fetchInvitableFriends = useCallback(async () => {
    if (!eventId || !eventService) return;
    setInviteFriendsLoading(true);
    try {
      const result = await eventService.getInvitableFriends(eventId);
      if (result.success) {
        setInvitableFriends(result.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch invitable friends:', err);
    } finally {
      setInviteFriendsLoading(false);
      setHasFetchedFriends(true);
    }
  }, [eventId, eventService]);

  // Fetch friends when modal opens
  useEffect(() => {
    if (isOpen && !hasFetchedFriends && eventId && eventService) {
      fetchInvitableFriends();
    }
  }, [isOpen, hasFetchedFriends, eventId, eventService, fetchInvitableFriends]);

  // Draw the share card now, not when the first scraper asks for it. Somebody who taps SHARE
  // pastes the link into WhatsApp seconds later, and that app's phone client gives up on a
  // preview image that is still rendering — so the render is kicked off here (the native app
  // never goes through server.js, which does the same for the web page), and the API caches it.
  useEffect(() => {
    if (!isOpen || !cardUrl || typeof Image === 'undefined') return;
    const img = new Image();
    img.src = cardUrl;
  }, [isOpen, cardUrl]);

  // And tell Facebook about the link now. Messenger draws its bubble from Facebook's cache of
  // the URL and does not wait for a crawl, so a link nobody primed goes out bare. The API
  // asks Facebook to scrape it (one call per link per window; a no-op without app keys).
  useEffect(() => {
    if (!isOpen || !url) return;
    api.post('/share/prime', { url }).catch(() => {});
  }, [isOpen, url]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setHasFetchedFriends(false);
      setSearchQuery('');
      setSearchResults([]);
      handleIgClose();
    }
  }, [isOpen]);

  // Debounced search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    if (searchTimeout.current) clearTimeout(searchTimeout.current);

    searchTimeout.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const result = await friendService.searchPeople(searchQuery, 10);
        if (result.success) {
          setSearchResults(result.data || []);
        }
      } catch (err) {
        console.error('Failed to search people:', err);
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => {
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
    };
  }, [searchQuery]);

  const handleInviteFriend = async (userId) => {
    if (!eventId || !eventService) return;
    setInviteLoading((prev) => ({ ...prev, [userId]: true }));
    try {
      const result = await eventService.inviteFriend(eventId, userId);
      if (result.success) {
        setInvitableFriends((prev) =>
          prev.map((f) => (f.userId === userId ? { ...f, status: 'invited' } : f))
        );
        setSearchResults((prev) =>
          prev.map((p) => (p.userId === userId ? { ...p, status: 'invited' } : p))
        );
      }
    } catch (err) {
      console.error('Failed to invite friend:', err);
    } finally {
      setInviteLoading((prev) => ({ ...prev, [userId]: false }));
    }
  };

  const shareText = `You're invited to ${title} on Portal! Apply here: ${url}`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopyLinkSuccess(true);
      setTimeout(() => setCopyLinkSuccess(false), 1600);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleSMS = async () => {
    const smsBody = encodeURIComponent(shareText);
    if (isMobile) {
      window.location.href = isIOS ? `sms:&body=${smsBody}` : `sms:?body=${smsBody}`;
    } else {
      try {
        await navigator.clipboard.writeText(shareText);
        setCopyLinkSuccess(true);
        setTimeout(() => setCopyLinkSuccess(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  const handleWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, '_blank', 'noopener,noreferrer');
  };

  const handleX = () => {
    const encodedUrl = encodeURIComponent(url);
    const encodedText = encodeURIComponent(shareText);
    window.open(`https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`, '_blank', 'noopener,noreferrer');
  };

  const handleFacebook = () => {
    const encodedUrl = encodeURIComponent(url);
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`, '_blank', 'noopener,noreferrer');
  };

  // Load image as blob — proxy CDN URLs through backend to avoid CORS
  const API_URL = API_BASE;
  const loadImageFromUrl = (imageUrl) => {
    return new Promise(async (resolve, reject) => {
      try {
        // Route ANY cross-origin image through our proxy so it can be drawn on the
        // canvas without CORS tainting — this is what makes SCRAPED event posters
        // (on external domains) show up instead of a blank card.
        const isAbsolute = /^https?:\/\//i.test(imageUrl);
        const sameOrigin = isAbsolute && imageUrl.startsWith(window.location.origin);
        const fetchUrl = (isAbsolute && !sameOrigin)
          ? `${API_URL}/events/proxy-image?url=${encodeURIComponent(imageUrl)}`
          : imageUrl;
        const resp = await fetch(fetchUrl);
        const blob = await resp.blob();
        const objectUrl = URL.createObjectURL(blob);
        const img = new Image();
        img.onload = () => { URL.revokeObjectURL(objectUrl); resolve(img); };
        img.onerror = () => { URL.revokeObjectURL(objectUrl); reject(new Error('Image load failed')); };
        img.src = objectUrl;
      } catch (err) {
        reject(err);
      }
    });
  };

  // The story image is the same card a Portal or Party Starter shares (StoryCard), mounted off screen with
  // its poster and QR already inlined so the capture has no cross-origin image to trip on.
  const storyRef = useRef(null);
  const [storyAssets, setStoryAssets] = useState(null); // { image, qr }
  const shareDefaults = useShareTemplates();
  const [storyPick, setStoryPick] = useState(null);
  const storyTemplate = storyPick || shareDefaults?.story || 'ring';
  const [printPick, setPrintPick] = useState(null);
  const printTemplate = printPick || shareDefaults?.print || 'ring';
  const [printPoster, setPrintPoster] = useState(null);

  const eventDateLine = date
    ? new Date(date).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).replace(/, (\d+:)/, ' · $1').toUpperCase()
    : '';
  const placeLine = [venue, city].filter(Boolean).join(' · ');

  const storyCardProps = {
    image: storyAssets?.image || null,
    fallback: 'door',
    topline: 'YOU’RE ',
    keyword: 'INVITED',
    words: igInclude.title ? (title || '') : '',
    showWords: igInclude.title,
    showLogo: igInclude.branding,
    meta: [igInclude.date ? eventDateLine : null, placeLine || null].filter(Boolean).join(' · '),
    qr: igInclude.qr ? storyAssets?.qr : null,
    code: igInclude.code ? (inviteCode || '') : '',
    cta: 'GET TICKETS →',
    ctaKind: 'white',
  };

  const toDataUrl = (blob) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

  const loadStoryAssets = async () => {
    if (storyAssets) return storyAssets;
    let image = null;
    if (posterUrl) {
      try {
        const isAbsolute = /^https?:\/\//i.test(posterUrl);
        const sameOrigin = isAbsolute && posterUrl.startsWith(window.location.origin);
        const resp = await fetch(isAbsolute && !sameOrigin ? `${API_URL}/events/proxy-image?url=${encodeURIComponent(posterUrl)}` : posterUrl);
        const blob = await resp.blob();
        // A refused proxy answers with text; only a real picture goes on the card.
        if (resp.ok && (blob.type || '').startsWith('image/')) image = await toDataUrl(blob);
      } catch { /* the card falls back to the door */ }
    }
    let qr = null;
    try { qr = await toDataUrl(await brandedQrBlob(url, 400, { margin: 12 })); } catch { /* no QR on the card */ }
    const assets = { image, qr };
    setStoryAssets(assets);
    return assets;
  };

  const handleInstagram = async () => {
    setIgGenerating(true);
    try {
      await loadStoryAssets();
      // The card has to have painted with the latest toggles and its images decoded.
      await new Promise((r) => setTimeout(r, 160));
      if (!storyRef.current) throw new Error('story card not mounted');
      const blob = await htmlToImage.toBlob(storyRef.current, { width: 1080, height: 1920, pixelRatio: 1, cacheBust: false, backgroundColor: '#000' });
      if (!blob) throw new Error('render failed');
      const imageUrl = URL.createObjectURL(blob);
      const fileName = `${(title || 'event').replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}-portal.png`;
      setIgPreview({ imageUrl, blob, fileName });
    } catch (err) {
      console.error('Failed to generate story image:', err);
    } finally {
      setIgGenerating(false);
    }
  };

  // Re-generate the story image when the toggles or template change (only while a
  // preview is already on screen).
  useEffect(() => {
    if (igPreview) handleInstagram();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [igInclude, storyTemplate]);

  const handleIgShare = async () => {
    if (!igPreview) return;
    setIgShareError('');
    try {
      // No text or url alongside the image: Instagram Stories and several other
      // targets drop the picture when a link rides with it, and the picture is
      // the entire point of this sheet.
      await shareImage({
        blob: igPreview.blob,
        fileName: igPreview.fileName,
        imageUrl: igPreview.imageUrl,
        title: 'Portal',
      });
    } catch (err) {
      // This used to be a console.error, which on a phone is the same as
      // nothing — the button looked broken with no way to find out why.
      setIgShareError(err?.message || 'Could not open the share sheet.');
    }
  };

  const handleIgSave = async () => {
    if (!igPreview) return;
    setIgShareError('');
    try {
      await saveImage({ blob: igPreview.blob, fileName: igPreview.fileName, imageUrl: igPreview.imageUrl });
    } catch (err) {
      setIgShareError(err?.message || 'Could not save the image.');
    }
  };

  const handleIgClose = () => {
    if (igPreview?.imageUrl) URL.revokeObjectURL(igPreview.imageUrl);
    setIgPreview(null);
    setIgShareError('');
  };


  // ── Mount / unmount with the exit animation ─────────────────────────────────────────────
  const phone = useMediaQuery('(max-width: 639px)');
  const reduced = usePrefersReducedMotion();
  const [mounted, setMounted] = useState(isOpen);
  const [closing, setClosing] = useState(false);
  const [burst, setBurst] = useState(null); // { x, y } while the burst plays
  const closeRef = useRef(null);
  const returnFocusRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      returnFocusRef.current = document.activeElement;
      setMounted(true);
      setClosing(false);
      if (originRect && !reduced) {
        setBurst({ x: originRect.left + originRect.width / 2, y: originRect.top + originRect.height / 2 });
        const t = setTimeout(() => setBurst(null), 800);
        return () => clearTimeout(t);
      }
      return undefined;
    }
    if (!mounted) return undefined;
    setClosing(true);
    const t = setTimeout(() => {
      setMounted(false);
      setClosing(false);
      try { returnFocusRef.current?.focus?.(); } catch { /* gone */ }
    }, reduced ? 150 : 240);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Lock the page behind it, and put focus on ✕.
  usePageScrollLock(mounted);
  useEffect(() => {
    if (!mounted) return undefined;
    const t = setTimeout(() => closeRef.current?.focus?.(), 50);
    return () => clearTimeout(t);
  }, [mounted]);

  // ── Send to friends ─────────────────────────────────────────────────────────────────────
  const [friends, setFriends] = useState([]);
  const [friendQuery, setFriendQuery] = useState('');
  const [picked, setPicked] = useState([]);
  const [beat, setBeat] = useState(null); // { names, title } while the send beat plays
  const [sendResult, setSendResult] = useState(null); // 'sent' | { error }
  const sendingNow = !!beat;

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    Promise.resolve(friendService.getFriends()).then((r) => {
      if (!cancelled && r?.success) setFriends((r.data || []).filter((f) => f.userId));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [isOpen]);
  useEffect(() => { if (!isOpen) { setPicked([]); setSendResult(null); setFriendQuery(''); } }, [isOpen]);

  const shownFriends = useMemo(() => {
    const q = friendQuery.trim().toLowerCase();
    return q ? friends.filter((f) => `${f.displayName || ''} ${f.handle || ''}`.toLowerCase().includes(q)) : friends;
  }, [friends, friendQuery]);
  const togglePick = (id) => { setSendResult(null); setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id])); };
  const firstName = (f) => (f?.displayName || f?.handle || '').split(' ')[0];
  const sendLabel = picked.length === 1
    ? `SEND TO ${firstName(friends.find((f) => f.userId === picked[0])).toUpperCase()}`
    : `SEND TO ${picked.length} FRIENDS`;

  // One 1:1 thread each, the same link card SendToFriendsPanel sends. The beat always plays its
  // full 1.8s (600ms under reduced motion); the outcome lands after it.
  const sendToFriends = async () => {
    if (!picked.length || sendingNow) return;
    const names = picked.map((id) => firstName(friends.find((f) => f.userId === id))).filter(Boolean);
    setSendResult(null);
    setBeat({ names, title: 'SENDING THE EVENT' });
    const payload = { kind: 'link', body: title ? `Check this out: ${title}` : 'Check this out', linkUrl: url };
    const work = (async () => {
      let ok = 0;
      let firstError = null;
      for (const userId of picked) {
        // eslint-disable-next-line no-await-in-loop
        const thread = await messageService.createOrGetThread(userId);
        if (!thread?.success || !thread.data?.id) { firstError = firstError || thread?.error; continue; }
        // eslint-disable-next-line no-await-in-loop
        const sent = await messageService.sendMessage(thread.data.id, payload);
        if (sent?.success) ok += 1; else firstError = firstError || sent?.error;
      }
      return { ok, firstError };
    })();
    const wait = (ms) => new Promise((r) => setTimeout(r, ms));
    if (!reduced) { await wait(1100); setBeat((b) => (b ? { ...b, title: 'ON ITS WAY' } : b)); await wait(400); }
    else await wait(600);
    setBeat((b) => (b ? { ...b, leaving: true } : b));
    await wait(reduced ? 0 : 300);
    const { ok, firstError } = await work;
    setBeat(null);
    if (ok === picked.length) { setPicked([]); setSendResult('sent'); }
    else if (ok > 0) { setPicked([]); setSendResult({ error: `Sent to ${ok} of ${picked.length}.` }); }
    else setSendResult({ error: firstError || "Couldn't send." });
  };

  const requestClose = useCallback(() => { if (!sendingNow) onClose?.(); }, [sendingNow, onClose]);
  useEffect(() => {
    if (!mounted) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !igPreview) requestClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mounted, requestClose, igPreview]);

  // Swipe down on the handle / header closes the phone sheet.
  const drag = useRef(null);
  const [dragY, setDragY] = useState(0);
  const dragHandlers = phone ? {
    onPointerDown: (e) => { drag.current = e.clientY; },
    onPointerMove: (e) => { if (drag.current != null) setDragY(Math.max(0, e.clientY - drag.current)); },
    onPointerUp: () => { if (dragY > 80) requestClose(); drag.current = null; setDragY(0); },
    onPointerCancel: () => { drag.current = null; setDragY(0); },
  } : {};

  const [posterMenu, setPosterMenu] = useState(false);
  // The poster for the print sheet, once someone asks for one.
  useEffect(() => {
    if (!posterMenu || !posterUrl || printPoster) return undefined;
    let on = true;
    loadImageFromUrl(posterUrl).then((img) => { if (on) setPrintPoster(img); }).catch(() => {});
    return () => { on = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posterMenu, posterUrl]);

  if (!mounted) return null;

  // ── Motion ──────────────────────────────────────────────────────────────────────────────
  const EO = 'cubic-bezier(0,0,.2,1)';
  const anim = (a) => (reduced ? 'none' : a);
  const scrimAnim = closing ? `ssFadeOut ${reduced ? 150 : 220}ms linear both` : `ssFadeIn ${reduced ? 150 : 260}ms linear both`;
  const sheetAnim = reduced
    ? (closing ? 'ssFadeOut 150ms linear both' : 'ssFadeIn 150ms linear both')
    : phone
      ? (closing ? 'ssSheetDown 240ms cubic-bezier(.4,0,1,1) both' : 'ssSheetUp 440ms cubic-bezier(.2,.7,.2,1) 80ms both')
      : (closing ? 'ssModalOut 200ms cubic-bezier(.4,0,1,1) both' : `ssModalIn 380ms ${EO} 120ms both`);
  const rise = (delay) => anim(`ssRise 320ms ${EO} ${delay}ms both`);

  const statusTint = { selling: '#A8D8EA', funding: '#FFCCE6', greenlit: '#B9F2C9', processing_greenlight: '#FFF1B8', ended: '#C6B8FF', canceled: '#FECACA', dissolved: '#FECACA' }[status] || '#F2F2F2';
  const dateLine = date
    ? new Date(date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).toUpperCase()
    : 'DATE TBD';
  const shownUrl = (url || '').replace(/^https?:\/\//, '');

  const socials = [
    { label: 'SMS', onClick: handleSMS, icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" strokeLinejoin="miter"><path d="M3 4h18v13H8l-5 4z" /><path d="M8 10.5h.01M12 10.5h.01M16 10.5h.01" /></svg> },
    { label: 'WHATSAPP', onClick: handleWhatsApp, icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg> },
    { label: 'X', onClick: handleX, icon: <svg width="19" height="19" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg> },
    { label: 'FACEBOOK', onClick: handleFacebook, icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" /></svg> },
    { label: igGenerating ? '…' : 'STORY', onClick: handleInstagram, icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" /></svg> },
  ];

  const pill = 'rounded-full font-mono font-bold';
  const label = (t) => <span className="font-mono text-[10px] font-bold tracking-[0.18em]" style={{ color: '#6B7280' }}>{t}</span>;
  const canInvite = eventId && eventService && (status === 'selling' || status === 'funding' || status === 'greenlit');
  const inviteRows = searchQuery.trim()
    ? searchResults.map((p) => ({ ...p, status: invitableFriends.find((f) => f.userId === p.userId)?.status || 'none' }))
    : invitableFriends;

  return createPortal(
    <>
      <style>{`
        @keyframes ssSpin { to { transform: rotate(-360deg) } }
        @keyframes ssFadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes ssFadeOut { from { opacity: 1 } to { opacity: 0 } }
        @keyframes ssPop { from { opacity: 0; transform: scale(.4) } to { opacity: 1; transform: scale(1) } }
        @keyframes ssRipple { from { opacity: .9; transform: scale(1) } to { opacity: 0; transform: scale(2.1) } }
        @keyframes ssRise { from { opacity: 0; transform: translateY(8px) } to { opacity: 1; transform: none } }
        @keyframes ssDrift { from { transform: scale(1.08) } to { transform: scale(1.18) } }
        @keyframes ssBurst { from { opacity: 1; transform: translate(-50%,-50%) scale(1) rotate(0) } to { opacity: 0; transform: translate(-50%,-50%) scale(7) rotate(-120deg) } }
        @keyframes ssBurstRipple { from { opacity: .9; transform: translate(-50%,-50%) scale(1) } to { opacity: 0; transform: translate(-50%,-50%) scale(5) } }
        @keyframes ssSheetUp { from { transform: translateY(100%) } to { transform: translateY(0) } }
        @keyframes ssSheetDown { from { transform: translateY(0) } to { transform: translateY(100%) } }
        @keyframes ssModalIn { from { opacity: 0; transform: translateY(16px) scale(.94) } to { opacity: 1; transform: none } }
        @keyframes ssModalOut { from { opacity: 1; transform: none } to { opacity: 0; transform: translateY(10px) scale(.97) } }
      `}</style>

      {/* The sheet */}
      <div role="dialog" aria-modal="true" aria-label="Share event" className="pr-overlay-root fixed inset-0 safe-inset z-[110] flex justify-center font-mono" style={{ alignItems: phone ? 'flex-end' : 'center' }} data-testid="share-sheet">
        <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,.6)', animation: scrimAnim }} onClick={requestClose} data-testid="share-scrim" />
        <div
          className="relative flex flex-col overflow-hidden bg-white text-black"
          style={{
            width: phone ? '100%' : 480,
            maxHeight: phone ? '88vh' : 'min(820px, 92vh)',
            boxShadow: phone ? 'none' : '4px 4px 0 0 #000',
            animation: sheetAnim,
            transform: dragY ? `translateY(${dragY}px)` : undefined,
          }}
        >
          <div {...dragHandlers} style={{ touchAction: phone ? 'none' : undefined }}>
            {phone && <div className="flex justify-center pt-2.5"><span className="block rounded-sm" style={{ width: 40, height: 4, background: '#D4D4D4' }} /></div>}
            <div className="flex items-center justify-between" style={{ padding: '16px 20px 14px' }}>
              <span className="text-[15px] font-bold tracking-[0.12em]">SHARE EVENT</span>
              <button
                ref={closeRef}
                type="button"
                onClick={requestClose}
                aria-label="Close"
                className="flex h-9 w-9 items-center justify-center rounded-full text-black transition-colors duration-200 hover:bg-black hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2"
                style={{ background: '#F2F2F2' }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square"><path d="M5 5l14 14M19 5L5 19" /></svg>
              </button>
            </div>
          </div>

          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto" style={{ padding: '0 20px 24px', gap: 22, overscrollBehavior: 'contain' }}>
            {/* 1 · The event */}
            <div className="flex items-center gap-4" style={{ animation: rise(140) }}>
              <SheetRing size={76} cover={posterUrl} ripple reduced={reduced} />
              <div className="flex min-w-0 flex-col gap-1.5">
                <span className="text-[15px] font-bold uppercase leading-tight tracking-[0.04em]">{title}</span>
                <span className="text-[11px] tracking-[0.1em]" style={{ color: '#6B7280' }}>{dateLine}</span>
                <div className="flex gap-1.5">
                  <span className={`${pill} text-[9px] tracking-[0.14em]`} style={{ padding: '3px 8px', background: statusTint }}>{(status || 'draft').replace('_', ' ').toUpperCase()}</span>
                  <span className={`${pill} text-[9px] tracking-[0.14em]`} style={{ padding: '3px 8px', background: '#F2F2F2' }}>{mode === 'curated' ? 'CURATED' : 'OPEN'}</span>
                </div>
              </div>
            </div>

            {/* 2 · Link */}
            <div className="flex flex-col gap-2" style={{ animation: rise(200) }}>
              {label('EVENT LINK')}
              <div className="flex h-12 items-center gap-2.5 rounded-full" style={{ background: '#F2F2F2', padding: '0 4px 0 18px' }}>
                <span className="min-w-0 flex-1 truncate text-[12px]">{shownUrl}</span>
                <button
                  type="button"
                  onClick={handleCopyLink}
                  className={`${pill} h-10 flex-shrink-0 px-4 text-[11px] tracking-[0.14em] transition-colors duration-200`}
                  style={{ background: copyLinkSuccess ? '#00FFFF' : '#000', color: copyLinkSuccess ? '#000' : '#fff' }}
                >
                  {copyLinkSuccess ? 'COPIED' : 'COPY LINK'}
                </button>
              </div>
            </div>

            {/* 3 · Friends */}
            <div className="flex flex-col gap-2.5" style={{ animation: rise(260) }}>
              {label('SEND TO FRIENDS ON PORTAL')}
              <label className="flex h-11 items-center gap-2.5 rounded-full px-4" style={{ background: '#F2F2F2' }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2.25" strokeLinecap="square" aria-hidden><circle cx="11" cy="11" r="7" /><path d="M21 21l-5-5" /></svg>
                <input
                  value={friendQuery}
                  onChange={(e) => setFriendQuery(e.target.value)}
                  placeholder="Search friends…"
                  aria-label="Search friends"
                  autoComplete="off"
                  className="min-w-0 flex-1 border-0 bg-transparent p-0 text-[13px] text-black shadow-none outline-none ring-0 placeholder-hand-subtle focus:border-0 focus:ring-0"
                />
              </label>
              {friends.length === 0 ? (
                <p className="text-[11px]" style={{ color: '#6B7280' }}>No friends yet — add a few and you can send events straight to them.</p>
              ) : (
                <div className="flex gap-3.5 overflow-x-auto [&::-webkit-scrollbar]:hidden" style={{ padding: '6px 2px 4px', scrollbarWidth: 'none' }} data-testid="friend-row">
                  {shownFriends.map((f) => {
                    const on = picked.includes(f.userId);
                    return (
                      <button key={f.userId} type="button" onClick={() => togglePick(f.userId)} aria-pressed={on} className="flex w-16 flex-shrink-0 flex-col items-center gap-1.5">
                        <span className="relative block" style={{ width: 60, height: 60 }}>
                          {on && <span className="absolute inset-0 rounded-full" style={{ backgroundImage: `url(${ringArt})`, backgroundSize: 'cover', animation: anim(`ssPop 260ms ${EO} both`) }} />}
                          <span
                            className="absolute flex items-center justify-center overflow-hidden rounded-full text-[16px] font-bold text-black"
                            style={{ inset: on ? '5.5%' : 0, background: f.avatarUrl ? '#1A1A1A' : tintFor(f.userId), transition: reduced ? 'none' : 'inset .2s cubic-bezier(.2,.7,.2,1)' }}
                          >
                            {f.avatarUrl ? <img src={f.avatarUrl} alt="" className="block h-full w-full object-cover" /> : (f.displayName || '?').charAt(0).toUpperCase()}
                          </span>
                          {on && (
                            <span className="absolute flex items-center justify-center rounded-full" style={{ right: -2, bottom: -2, width: 20, height: 20, background: '#00FFFF', border: '2px solid #fff', animation: anim(`ssPop 220ms ${EO} 80ms both`) }}>
                              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="4" strokeLinecap="square"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
                            </span>
                          )}
                        </span>
                        <span className="max-w-16 truncate text-[10px] text-black" style={{ fontWeight: on ? 700 : 400 }}>{firstName(f)}</span>
                      </button>
                    );
                  })}
                </div>
              )}
              {picked.length > 0 && (
                <button
                  type="button"
                  onClick={sendToFriends}
                  className={`${pill} h-12 text-[12px] tracking-[0.16em] text-white`}
                  style={{ border: '2px solid transparent', background: 'linear-gradient(#000,#000) padding-box, linear-gradient(90deg,#FD199F,#FF33C6,#B5B1FF,#127BFD,#22ECFB,#FAE1B4,#FCAA6D,#FD199F) border-box', animation: anim(`ssRise 240ms ${EO} both`) }}
                >
                  {sendLabel}
                </button>
              )}
              {sendResult === 'sent' && (
                <span className="text-[11px] font-bold tracking-[0.14em]" style={{ animation: anim(`ssRise 240ms ${EO} both`) }}>SENT. IT&apos;S IN THEIR INBOX.</span>
              )}
              {sendResult?.error && (
                <span className="flex items-center gap-3 text-[11px] font-bold tracking-[0.14em]" role="alert">
                  {sendResult.error.toUpperCase()}
                  {picked.length > 0 && <button type="button" onClick={sendToFriends} className="underline">RETRY</button>}
                </span>
              )}
              <p className="m-0 text-[11px] leading-normal" style={{ color: '#6B7280' }}>Lands in their Portal inbox with a link they can open and grab a spot from.</p>
            </div>

            {/* 4 · Socials */}
            <div className="grid grid-cols-5 gap-2" style={{ animation: rise(320) }}>
              {socials.map((t) => (
                <button key={t.label} type="button" onClick={t.onClick} className="group flex flex-col items-center gap-2 text-black">
                  <span className="flex h-[52px] w-[52px] items-center justify-center rounded-full transition-colors duration-200 group-hover:bg-black group-hover:text-white" style={{ background: '#F2F2F2' }}>{t.icon}</span>
                  <span className="text-[9px] font-bold tracking-[0.12em]">{t.label}</span>
                </button>
              ))}
            </div>

            {/* 5 · QR & posters */}
            <div className="flex items-start gap-4 p-3.5" style={{ background: '#F2F2F2', animation: rise(380) }}>
              <span className="flex h-[76px] w-[76px] flex-shrink-0 items-center justify-center bg-white"><BrandedQR value={url} size={66} /></span>
              <div className="flex min-w-0 flex-col gap-2">
                <span className="text-[11px] font-bold tracking-[0.14em]">QR CODE &amp; POSTERS</span>
                <span className="text-[11px] leading-normal" style={{ color: '#6B7280' }}>A print-ready poster with the Portal QR. Pin it up, hand it out, or drop it in a story.</span>
                <button type="button" onClick={() => setPosterMenu((o) => !o)} aria-expanded={posterMenu} className="self-start text-[11px] font-bold tracking-[0.12em] underline">DOWNLOAD POSTER</button>
                {posterMenu && (
                  <div className="flex flex-col gap-1.5">
                    <ShareTemplatePicker
                      kind="print"
                      value={printTemplate}
                      onChange={setPrintPick}
                      onDark={false}
                      renderPreview={(t) => <PosterThumb template={t} poster={printPoster} details={{ title, dateLabel: eventDateLine, venue, city, url, code: inviteCode }} />}
                    />
                    {Object.entries(POSTER_SIZES).map(([key, { label: l }]) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => handleDownloadPoster(key)}
                        disabled={!!posterBusy}
                        className={`${pill} flex items-center justify-between bg-white px-3.5 py-2 text-[10px] tracking-[0.1em] transition-colors duration-200 hover:bg-black hover:text-white disabled:opacity-50`}
                      >
                        <span>{l.toUpperCase()}</span>
                        <span>{posterBusy === key ? 'BUILDING…' : 'DOWNLOAD'}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* The host's invite list — who can get in (curated events), unchanged in what it does. */}
            {canInvite && (
              <div className="flex flex-col gap-2.5" style={{ animation: rise(440) }}>
                {label('INVITE FRIENDS')}
                <label className="flex h-11 items-center gap-2.5 rounded-full px-4" style={{ background: '#F2F2F2' }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2.25" strokeLinecap="square" aria-hidden><circle cx="11" cy="11" r="7" /><path d="M21 21l-5-5" /></svg>
                  <input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by name or handle…"
                    aria-label="Search people to invite"
                    autoComplete="off"
                    className="min-w-0 flex-1 border-0 bg-transparent p-0 text-[13px] text-black shadow-none outline-none ring-0 placeholder-hand-subtle focus:border-0 focus:ring-0"
                  />
                </label>
                {(inviteFriendsLoading || searchLoading) ? (
                  <div className="flex justify-center py-3"><LoadingSpinner size="sm" /></div>
                ) : inviteRows.length === 0 ? (
                  <p className="text-[11px]" style={{ color: '#6B7280' }}>{searchQuery.trim() ? `No results for “${searchQuery}”` : 'Add friends to invite them to your events.'}</p>
                ) : (
                  <div className="flex flex-col gap-1.5">
                    {inviteRows.map((p) => (
                      <div key={p.userId} className="flex items-center gap-3 rounded-full py-1.5 pl-1.5 pr-2" style={{ background: '#F2F2F2' }}>
                        <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center overflow-hidden rounded-full text-[13px] font-bold" style={{ background: p.avatarUrl ? '#1A1A1A' : tintFor(p.userId) }}>
                          {p.avatarUrl ? <img src={p.avatarUrl} alt="" className="h-full w-full object-cover" /> : (p.displayName || '?').charAt(0).toUpperCase()}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-[13px] font-semibold">{p.displayName}</span>
                          {p.handle && <span className="block truncate text-[11px]" style={{ color: '#6B7280' }}>@{p.handle}</span>}
                        </span>
                        {p.status === 'none' ? (
                          <button type="button" onClick={() => handleInviteFriend(p.userId)} disabled={inviteLoading[p.userId]} className={`${pill} h-8 bg-black px-3.5 text-[10px] tracking-[0.12em] text-white disabled:opacity-50`}>
                            {inviteLoading[p.userId] ? '…' : 'INVITE'}
                          </button>
                        ) : (
                          <span className={`${pill} px-3 text-[10px] tracking-[0.12em]`} style={{ color: '#6B7280' }}>{p.status === 'invited' ? 'INVITED' : 'APPLIED'}</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* The send beat — the Portal send takeover, fitted to the sheet. */}
          {beat && (
            <div
              className="absolute inset-0 z-[5] flex flex-col items-center justify-center overflow-hidden bg-black"
              style={{ animation: beat.leaving ? `ssFadeOut ${reduced ? 150 : 300}ms cubic-bezier(.4,0,1,1) both` : `ssFadeIn 200ms linear both` }}
              data-testid="send-beat"
            >
              {posterUrl && <div className="absolute" style={{ inset: '-10%', backgroundImage: `url(${posterUrl})`, backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.35, animation: anim('ssDrift 1.8s linear both') }} />}
              <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,.55)' }} />
              <div className="relative flex flex-col items-center">
                <span className="relative block" style={{ width: 160, height: 160 }}>
                  {!reduced && <span className="absolute inset-0 rounded-full" style={{ border: '2px solid #00FFFF', animation: `ssRipple 1.5s ${EO} .35s infinite both` }} />}
                  {!reduced && <span className="absolute inset-0 rounded-full" style={{ border: '2px solid #00FFFF', animation: `ssRipple 1.5s ${EO} .85s infinite both` }} />}
                  <span className="absolute inset-0" style={{ animation: anim(`ssPop 420ms ${EO} both`) }}>
                    <span className="absolute inset-0 rounded-full" style={{ backgroundImage: `url(${ringArt})`, backgroundSize: 'cover', animation: anim('ssSpin 1.1s linear infinite') }} />
                  </span>
                  <span className="absolute overflow-hidden rounded-full" style={{ inset: '5.5%', background: '#1A1A1A', animation: anim(`ssPop 420ms ${EO} 120ms both`) }}>
                    {posterUrl && <img src={posterUrl} alt="" className="block h-full w-full object-cover" />}
                  </span>
                </span>
                <p className="m-0 text-center text-[14px] font-bold tracking-[0.1em] text-white" style={{ marginTop: 36, animation: anim(`ssRise 320ms ${EO} 300ms both`) }}>{beat.title}</p>
                <p className="m-0 text-center text-[10px] font-bold tracking-[0.1em]" style={{ marginTop: 10, color: 'rgba(255,255,255,.65)', animation: anim(`ssRise 320ms ${EO} 360ms both`) }}>
                  {[...beat.names.slice(0, 3), ...(beat.names.length > 3 ? [`+${beat.names.length - 3}`] : [])].join(' · ').toUpperCase()}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* The burst: the button's ring flying out of the card. Over everything, touching nothing. */}
      {burst && (
        <div aria-hidden className="pointer-events-none fixed inset-0 safe-inset z-[120]" data-testid="share-burst">
          <span className="absolute rounded-full" style={{ left: burst.x, top: burst.y, width: 44, height: 44, backgroundImage: `url(${ringArt})`, backgroundSize: 'cover', animation: `ssBurst 560ms ${EO} both` }} />
          <span className="absolute rounded-full" style={{ left: burst.x, top: burst.y, width: 44, height: 44, border: '2px solid #00FFFF', animation: `ssBurstRipple 700ms ${EO} 60ms both` }} />
          <span className="absolute rounded-full" style={{ left: burst.x, top: burst.y, width: 44, height: 44, border: '2px solid #00FFFF', animation: `ssBurstRipple 700ms ${EO} 200ms both` }} />
        </div>
      )}

      {/* The Instagram Story preview sits over the sheet. */}
      {/* The card the story image is captured from: off screen, always at full size. */}
      {(igGenerating || igPreview) && (
        <div aria-hidden style={{ position: 'fixed', left: -20000, top: 0, width: 1080, height: 1920, pointerEvents: 'none' }}>
          <StoryCard {...storyCardProps} template={storyTemplate} refEl={storyRef} />
        </div>
      )}
      {igPreview && (
        <div className="fixed inset-0 safe-inset bg-black/80 z-[130] overflow-y-auto overscroll-contain" onClick={handleIgClose}>
          {/* min-h-full + flex centers the card when it fits and lets the whole
              card scroll (from the top) when it's taller than the viewport —
              justify-center alone clipped the top/bottom with no way to reach it. */}
          <div className="min-h-full flex items-center justify-center p-4">
            <div className="max-w-sm w-full my-auto" onClick={(e) => e.stopPropagation()}>
              <div className="bg-black border-2 border-white p-4">
                <h3 className="font-mono text-sm font-bold uppercase tracking-wider text-white text-center mb-3">
                  YOUR INSTAGRAM STORY
                </h3>
                <img
                  src={igPreview.imageUrl}
                  alt="Story preview"
                  className="w-full max-h-[55vh] object-contain border border-white/20"
                />

              <div className="mt-3 flex justify-center">
                <ShareTemplatePicker
                  kind="story"
                  value={storyTemplate}
                  onChange={setStoryPick}
                  size={72}
                  renderPreview={(t) => <ScaledCard box={68}><StoryCard {...storyCardProps} template={t} /></ScaledCard>}
                />
              </div>

              {/* Toggle which text pieces are overlaid — uncheck anything the
                  poster already shows so we don't stack text over text. */}
              <div className="mt-3 border-t border-white/20 pt-3">
                <div className="mb-2 font-mono text-[10px] uppercase tracking-widest text-white/50">
                  Include on image {igGenerating && <span className="text-white/30">· updating…</span>}
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-2">
                  {[['branding', 'Logo'], ['title', 'Title'], ['date', 'Date'], ['code', 'Code'], ['qr', 'QR']].map(([key, label]) => (
                    <label key={key} className="flex cursor-pointer items-center gap-2 font-mono text-[11px] uppercase tracking-wide text-white">
                      <input
                        type="checkbox"
                        checked={igInclude[key]}
                        onChange={() => setIgInclude((p) => ({ ...p, [key]: !p[key] }))}
                        className="h-4 w-4 accent-white"
                      />
                      {label}
                    </label>
                  ))}
                </div>
                <p className="mt-2 font-mono text-[10px] text-white/40">Uncheck anything your poster already shows.</p>
              </div>

              {igShareError && (
                <div className="mt-3 border-2 border-red-500 px-2 py-1.5 font-mono text-[11px] uppercase tracking-wider text-red-400">
                  {igShareError}
                </div>
              )}

              <div className="flex gap-2 mt-4">
                <button
                  type="button"
                  onClick={handleIgShare}
                  className="flex-1 py-3 bg-white text-black font-mono text-xs font-bold uppercase tracking-wider hover:bg-gray-200 transition-colors"
                >
                  SHARE
                </button>
                {/* Only on the web. Natively there is no download, and no
                    permission-free way to write to the photo library — saving
                    happens inside the OS sheet, which is exactly what SHARE
                    already opens. Two buttons doing the identical thing is
                    worse than one. */}
                {!isNative() && (
                  <button
                    type="button"
                    onClick={handleIgSave}
                    className="flex-1 py-3 border-2 border-white text-white font-mono text-xs font-bold uppercase tracking-wider hover:bg-white hover:text-black transition-colors"
                  >
                    SAVE IMAGE
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={handleIgClose}
                className="w-full mt-2 py-2 text-gray-500 font-mono text-xs uppercase tracking-wider hover:text-white transition-colors"
              >
                CLOSE
              </button>
            </div>
            </div>
          </div>
        </div>
      )}
    </>,
    document.body,
  );
}
